import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const Canceryearly = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for CANCER
  const staticMeta = {
    title: "AstroBhagya – Free Cancer Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Cancer yearly horoscope predictions 2026? Discover free Cancer astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Cancer yearly horoscope predictions 2026, Cancer yearly Astrology predictions, Cancer annual predictions, Yearly horoscope by Cancer zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchCancerYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `cancer_yearly_${dateStr}`; // 🔥 CANCER cache key
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Find CANCER data (id: 33 or sunShine contains "CANCER") 🔥 ID=33
        const cancerData = data.find(
          item => item.id === 33 || item.sunShine?.toUpperCase().includes("CANCER")
        );

        if (cancerData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, cancerData);
          localStorage.setItem(cacheKey, JSON.stringify(cancerData));
          setHoroscopeData(cancerData);
        } else {
          throw new Error("Cancer data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Cancer yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Cancer Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchCancerYearly(retries - 1);
      } else {
        // FALLBACK DATA - CANCER 2026 
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Cancer 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>As 2026 begins, Cancer, the year brings a shift from emotional reflection to emotional control. Situations may require firm decisions where you previously avoided confrontation.</p>
            <h2>Love and Relationships</h2>
            <p>In committed relationships, focus on responsibility and trust. Love shown through actions rather than words strengthens reliability.</p>
            <h2>Career and Finances</h2>
            <p>Career demands accountability. Leadership roles test patience. Financial security through careful budgeting and avoiding emotional spending.</p>
            <h2>Family and Home Life</h2>
            <p>Family matters take priority. Set healthy emotional boundaries while supporting loved ones.</p>
            <h2>Health & Wellness</h2>
            <p>Health depends on routine. Stress management through journaling and meditation essential.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Wear Pearl or Moonstone:</strong> Calms emotional fluctuations and improves clarity.</li>
              <li><strong>Monday Remedies:</strong> Offering milk/rice strengthens lunar energy.</li>
              <li><strong>Charity:</strong> Donating food/water promotes emotional healing.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCancerYearly();
  }, [fetchCancerYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Cancer 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchCancerYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Cancer 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Cancerrr.webp")}
                alt="Cancer"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/gemini" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Gemini Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/leo" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Leo Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS ARIES/TAURUS/GEMINI */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Cancer Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Cancer?",
                  answer: "Cancer individuals are known for their emotional depth, nurturing nature, and strong intuition. Ruled by the Moon, they value family, security, and emotional connections.",
                },
                {
                  question: "2. What can Cancer expect in love and relationships in 2026?",
                  answer: "In 2026, Cancer relationships focus on responsibility and trust. Love shown through actions strengthens reliability and emotional security.",
                },
                {
                  question: "3. What does 2026 look like for Cancer career growth?",
                  answer: "Career demands accountability and structure. Leadership roles test patience. Emotional decision-making avoided for logical planning.",
                },
                {
                  question: "4. What health advice for Cancer in 2026?",
                  answer: "Health depends on routine and emotional discipline. Journaling, meditation, and structured daily routines stabilize well-being.",
                },
                {
                  question: "5. Key astrological remedies for Cancer?",
                  answer: "Wear Pearl/Moonstone, Monday remedies with milk/rice, and food/water charity promote emotional healing.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Canceryearly; 
